import React, { useCallback, useEffect, useState } from "react";
import {
  FaCog,
  FaPlus,
  FaTrash,
  FaEdit,
  FaExternalLinkAlt,
  FaSync,
  FaBars,
  FaArrowUp,
  FaArrowDown,
} from "react-icons/fa";
import { useLocation, useNavigate } from "react-router-dom";
import { apihubDelete, apihubGet, apihubPost } from "../../api/apihub";
import { useProjectPhone } from "../../hooks/useProjectPhone";

const FIELD_TYPES = [
  { value: "text", label: "Text" },
  { value: "phone", label: "Phone" },
  { value: "email", label: "Email" },
  { value: "number", label: "Number" },
  { value: "dropdown", label: "Dropdown" },
  { value: "paragraph", label: "Paragraph" },
];

const newField = (overrides = {}) => ({
  key: `f-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  title: "",
  type: "text",
  required: false,
  role: "",
  options: "",
  ...overrides,
});

const defaultFields = () => [
  newField({ title: "Customer Name", type: "text", required: true, role: "name" }),
  newField({ title: "Mobile Number", type: "phone", required: true, role: "phone" }),
];

const emptyForm = () => ({
  formname: "",
  formurl: "",
  formid: "",
  description: "",
  fields: defaultFields(),
  sheetMode: "new",
  spreadsheetId: "",
  spreadsheetUrl: "",
  callApi: false,
  templateId: "",
  templateName: "",
});

const fieldsFromRow = (row) => {
  const list = Array.isArray(row?.fields) ? row.fields : [];
  if (!list.length) {
    return [
      newField({ title: "Name", type: "text", required: true, role: "name" }),
      newField({ title: "WhatsApp Number", type: "phone", required: true, role: "phone" }),
    ];
  }
  return list.map((field) =>
    newField({
      title: field.title || field.label || "",
      type: field.type || "text",
      required: Boolean(field.required),
      role: field.role || "",
      options: Array.isArray(field.options) ? field.options.join(", ") : field.options || "",
    }),
  );
};

const formatSyncTime = (value) => {
  if (!value) return "Never";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString();
};

const payloadFields = (fields) =>
  (fields || [])
    .map((field, index) => ({
      title: String(field.title || "").trim(),
      type: field.type || "text",
      required: Boolean(field.required),
      role: field.role || "",
      options: field.type === "dropdown" ? field.options || "" : "",
      order: index,
    }))
    .filter((field) => field.title);

const templatePreview = (tpl) => {
  if (!tpl) return null;
  const components = Array.isArray(tpl.components) ? tpl.components : [];
  const byType = (type) =>
    components.find((row) => String(row.type || "").toUpperCase() === type);
  const header = byType("HEADER");
  const body = byType("BODY");
  const footer = byType("FOOTER");
  const buttons = byType("BUTTONS");
  const headerFormat = String(header?.format || "").toUpperCase();
  return {
    name: tpl.name || tpl.template_name || "",
    language: tpl.language || "",
    category: tpl.category || "",
    headerText: header?.text || "",
    headerFormat,
    body: body?.text || "",
    footer: footer?.text || "",
    buttons: (buttons?.buttons || []).map((btn) => btn.text).filter(Boolean),
  };
};

const GoogleFormConfig = () => {
  const { projectId, phoneNoId } = useProjectPhone();
  const location = useLocation();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [sheets, setSheets] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [syncingId, setSyncingId] = useState("");

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setError("");
    try {
      const res = await apihubPost(
        `/getInteractedCustomerDetail/fetchallgoogleformconfig?page=${page}&limit=${limit}`,
        { projectId },
      );
      if (res?.success) {
        setRows(res.data || []);
        setTotalPages(res.pagination?.totalPages || 1);
      } else {
        setRows([]);
      }
    } catch (err) {
      setError(err.message || "Failed to fetch Google Form configs");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [projectId, page, limit]);

  const loadSheets = useCallback(async () => {
    if (!projectId) return;
    try {
      const res = await apihubPost(
        "/getInteractedCustomerDetail/fetchallgooglesheetconfig?page=1&limit=50",
        { projectId },
      );
      setSheets(res?.data || []);
    } catch {
      setSheets([]);
    }
  }, [projectId]);

  const loadTemplates = useCallback(async () => {
    if (!projectId) return;
    setLoadingTemplates(true);
    try {
      const qs = new URLSearchParams({
        page: "1",
        limit: "50",
        sortBy: "createdAt",
        sortOrder: "desc",
      });
      const phoneForPath = phoneNoId || "0";
      const res = await apihubGet(
        `/getInteractedCustomerDetail/gettotaltemplatesbyphonenumberid/${phoneForPath}?${qs}&projectId=${encodeURIComponent(
          projectId,
        )}`,
      );
      const list = (res?.data || []).filter((tpl) => {
        const status = String(tpl.template_status || "").toUpperCase();
        const category = String(tpl.category || "").toUpperCase();
        return status === "APPROVED" && category !== "AUTHENTICATION";
      });
      setTemplates(list);
    } catch {
      setTemplates([]);
    } finally {
      setLoadingTemplates(false);
    }
  }, [phoneNoId, projectId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const result = location.state?.googleForm;
    if (!result) return;
    if (result === "created") {
      setMessage("Google Form created. New submissions will sync automatically.");
      setError("");
      load();
    } else if (result === "connected") {
      setMessage(location.state?.message || "Google account connected. Sync responses or wait for the next poll.");
      setError("");
      load();
    } else if (result === "denied") {
      setError("Google authorization was cancelled.");
    } else {
      setError(location.state?.message || "Failed to create Google Form.");
    }
    navigate(location.pathname, { replace: true, state: {} });
  }, [load, location.pathname, location.state, navigate]);

  useEffect(() => {
    if (!modalOpen) return;
    loadSheets();
    if (form.callApi) loadTemplates();
  }, [modalOpen, form.callApi, loadSheets, loadTemplates]);

  const closeEditor = () => {
    setModalOpen(false);
    setEditId(null);
    setForm(emptyForm());
    setError("");
  };

  const openCreate = () => {
    setEditId(null);
    setForm(emptyForm());
    setModalOpen(true);
    setMessage("");
    setError("");
  };

  const openEdit = (row) => {
    setEditId(row._id);
    setForm({
      formname: row.formname || "",
      formurl: row.formurl || "",
      formid: row.formid || "",
      description: row.description || "",
      fields: fieldsFromRow(row),
      sheetMode: row.sheetMode || (row.spreadsheetId ? "existing" : ""),
      spreadsheetId: row.spreadsheetId || "",
      spreadsheetUrl: row.spreadsheetUrl || "",
      callApi: Boolean(row.callApi),
      templateId: row.templateId || "",
      templateName: row.templateName || "",
    });
    setModalOpen(true);
  };

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const setField = (key, patch) => {
    setForm((prev) => ({
      ...prev,
      fields: prev.fields.map((field) => {
        if (field.key !== key) {
          if (patch.role && patch.role === field.role) {
            return { ...field, role: "" };
          }
          return field;
        }
        return { ...field, ...patch };
      }),
    }));
  };

  const addField = () => {
    setForm((prev) => {
      if (prev.fields.length >= 15) return prev;
      return { ...prev, fields: [...prev.fields, newField()] };
    });
  };

  const removeField = (key) => {
    setForm((prev) => ({
      ...prev,
      fields: prev.fields.filter((field) => field.key !== key),
    }));
  };

  const moveField = (index, direction) => {
    setForm((prev) => {
      const next = [...prev.fields];
      const target = index + direction;
      if (target < 0 || target >= next.length) return prev;
      const [item] = next.splice(index, 1);
      next.splice(target, 0, item);
      return { ...prev, fields: next };
    });
  };

  const onSheetMode = (mode) => {
    setForm((prev) => ({
      ...prev,
      sheetMode: mode,
      spreadsheetId: mode === "new" ? "" : prev.spreadsheetId,
      spreadsheetUrl: mode === "new" ? "" : prev.spreadsheetUrl,
    }));
  };

  const onExistingSheet = (e) => {
    const id = e.target.value;
    const selected = sheets.find((sheet) => String(sheet.spreadsheetid || sheet.spreadsheetId) === id);
    setForm((prev) => ({
      ...prev,
      spreadsheetId: id,
      spreadsheetUrl: selected?.sheeturl || selected?.sheetUrl || prev.spreadsheetUrl,
    }));
  };

  const onTemplate = (e) => {
    const id = e.target.value;
    const selected = templates.find((tpl) => String(tpl._id || tpl.id) === id);
    setForm((prev) => ({
      ...prev,
      templateId: id,
      templateName: selected?.name || "",
      callApi: true,
    }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.formname) {
      setError("Please enter a form name.");
      return;
    }
    if (editId && !form.formurl) {
      setError("Please fill required fields (form name, form URL).");
      return;
    }
    const fields = payloadFields(form.fields);
    if (!editId && fields.length < 2) {
      setError("Add at least a name and mobile number field.");
      return;
    }
    if (!editId && fields.some((field) => field.type === "dropdown" && !String(field.options || "").trim())) {
      setError("Add comma-separated options for each dropdown field.");
      return;
    }
    if (!editId && form.sheetMode === "existing" && !form.spreadsheetId && !form.spreadsheetUrl) {
      setError("Select an existing Google Sheet or paste a spreadsheet URL.");
      return;
    }
    if (form.callApi && !form.templateId && !form.templateName) {
      setError("Select an approved WhatsApp template.");
      return;
    }
    setSaving(true);
    setError("");
    setMessage("");
    try {
      if (editId) {
        const res = await apihubPost(
          "/getInteractedCustomerDetail/editgoogleformconfig",
          {
            projectid: projectId,
            activeeditid: editId,
            formname: form.formname,
            formurl: form.formurl,
            formid: form.formid,
            description: form.description,
            createLeads: true,
            startFlow: false,
            flowId: "",
            fields,
            sheetMode: form.sheetMode,
            spreadsheetId: form.spreadsheetId,
            spreadsheetUrl: form.spreadsheetUrl,
            callApi: form.callApi,
            templateId: form.callApi ? form.templateId : "",
            templateName: form.callApi ? form.templateName : "",
          },
        );
        if (res?.success === false) {
          throw new Error(res.message || "Save failed");
        }
        setMessage(res?.message || "Config updated");
        setModalOpen(false);
        setForm(emptyForm());
        setEditId(null);
        load();
        return;
      }

      const redirectUri = `${window.location.origin}/google-oauth-callback`;
      const returnTo = `${window.location.origin}/businessprojects/${encodeURIComponent(projectId)}/googleformconfig`;
      const res = await apihubPost(
        "/getInteractedCustomerDetail/googleformoauthstart",
        {
          projectId,
          formname: form.formname,
          description: form.description,
          fields,
          sheetMode: form.sheetMode,
          spreadsheetId: form.spreadsheetId,
          spreadsheetUrl: form.spreadsheetUrl,
          callApi: form.callApi,
          templateId: form.callApi ? form.templateId : "",
          templateName: form.callApi ? form.templateName : "",
          redirectUri,
          returnTo,
        },
      );
      if (res?.authUrl) {
        window.location.assign(res.authUrl);
        return;
      }
      if (res?.success === false) {
        throw new Error(res.message || "Failed to create Google Form");
      }
      setMessage(res?.message || "Google Form created successfully.");
      setModalOpen(false);
      setForm(emptyForm());
      load();
    } catch (err) {
      setError(err.message || "Failed to save config");
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this Google Form configuration?")) return;
    setError("");
    try {
      const res = await apihubDelete(
        `/getInteractedCustomerDetail/deletegoogleformconfig/${id}`,
      );
      if (res?.success === false) {
        throw new Error(res.message || "Delete failed");
      }
      setMessage(res?.message || "Deleted");
      load();
    } catch (err) {
      setError(err.message || "Failed to delete config");
    }
  };

  const onReconnect = async () => {
    setError("");
    setMessage("");
    try {
      const redirectUri = `${window.location.origin}/google-oauth-callback`;
      const returnTo = `${window.location.origin}/businessprojects/${encodeURIComponent(projectId)}/googleformconfig`;
      const res = await apihubPost(
        "/getInteractedCustomerDetail/googleformoauthreconnect",
        {
          projectId,
          redirectUri,
          returnTo,
        },
      );
      if (res?.authUrl) {
        window.location.assign(res.authUrl);
        return;
      }
      throw new Error(res?.message || "Failed to start Google reconnection");
    } catch (err) {
      setError(err.message || "Failed to reconnect Google");
    }
  };

  const onSync = async (id) => {
    setSyncingId(id);
    setError("");
    setMessage("");
    try {
      const res = await apihubPost(
        `/getInteractedCustomerDetail/syncgoogleformconfig/${id}`,
        { projectId },
      );
      if (res?.success === false) {
        throw new Error(res.message || "Sync failed");
      }
      setMessage(res?.message || "Responses synced");
      load();
    } catch (err) {
      setError(err.message || "Failed to sync form responses");
    } finally {
      setSyncingId("");
    }
  };

  const selectedTemplate =
    templates.find((tpl) => String(tpl._id || tpl.id) === String(form.templateId)) ||
    templates.find(
      (tpl) =>
        form.templateName &&
        String(tpl.name || "").toLowerCase() === String(form.templateName).toLowerCase(),
    ) ||
    null;
  const preview = templatePreview(selectedTemplate);

  return (
    <div className="biz-panel-page biz-smsconfig-page">
      <div className="biz-panel-header biz-panel-header-row">
        <h1>
          <FaCog />
          Google Form
        </h1>
        <div className="biz-table-actions">
          <button type="button" className="biz-btn-secondary" onClick={onReconnect}>
            Reconnect Google
          </button>
          <button type="button" className="biz-btn-primary" onClick={openCreate}>
            <FaPlus /> Create Google Form
          </button>
        </div>
      </div>

      <p className="biz-page-lead">
        Create a Google Form with custom fields, optionally save answers to a
        Sheet, and send an approved WhatsApp template when Call API is enabled.
      </p>

      {message && <p className="biz-banner biz-banner-success">{message}</p>}
      {error && !modalOpen && (
        <p className="biz-banner biz-banner-error">{error}</p>
      )}

      <div className="biz-table-wrap">
        {loading ? (
          <p className="biz-table-message">Loading Google Form configurations…</p>
        ) : (
          <table className="biz-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Form Name</th>
                <th>Form URL</th>
                <th>Last sync</th>
                <th>On submit</th>
                <th className="biz-col-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={6} className="biz-table-empty">
                    No Google Form configurations yet.
                  </td>
                </tr>
              ) : (
                rows.map((row, idx) => (
                  <tr key={row._id}>
                    <td>{(page - 1) * limit + idx + 1}</td>
                    <td>{row.formname}</td>
                    <td className="biz-table-mono">
                      {row.formurl ? (
                        <a
                          href={row.formurl}
                          target="_blank"
                          rel="noreferrer"
                          title="Open form"
                        >
                          {row.formurl} <FaExternalLinkAlt />
                        </a>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td>{formatSyncTime(row.lastSyncedAt)}</td>
                    <td>
                      {row.callApi
                        ? row.templateName || "WhatsApp template"
                        : row.spreadsheetId
                          ? "Google Sheet"
                          : "—"}
                    </td>
                    <td className="biz-col-actions">
                      <div className="biz-table-actions">
                        <button
                          type="button"
                          className="biz-btn-icon"
                          title="Sync new form responses"
                          disabled={syncingId === row._id}
                          onClick={() => onSync(row._id)}
                        >
                          <FaSync className={syncingId === row._id ? "fa-spin" : ""} />
                        </button>
                        <button
                          type="button"
                          className="biz-btn-icon"
                          title="Edit"
                          onClick={() => openEdit(row)}
                        >
                          <FaEdit />
                        </button>
                        <button
                          type="button"
                          className="biz-btn-icon biz-btn-icon-danger"
                          title="Delete"
                          onClick={() => onDelete(row._id)}
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

      {totalPages > 1 && (
        <div className="biz-pagination">
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
          >
            Previous
          </button>
          <span>
            Page {page} of {totalPages}
          </span>
          <button
            type="button"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </button>
        </div>
      )}

      {modalOpen && (
        <div className="wa-flow-builder-overlay" role="dialog" aria-modal="true">
          <form className="wa-conv-flow-shell gform-builder-shell" onSubmit={onSubmit}>
            <header className="wa-flow-builder-top">
              <div>
                <h2>{editId ? "Edit Google Form" : "Create Google Form"}</h2>
                <p>
                  Add fields, choose a Google Sheet destination, and send an
                  approved WhatsApp template when a response is submitted.
                </p>
              </div>
              <div className="wa-flow-builder-top-actions">
                <button
                  type="button"
                  className="biz-btn-secondary"
                  onClick={closeEditor}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button type="submit" className="biz-btn-primary" disabled={saving}>
                  {saving
                    ? editId
                      ? "Saving…"
                      : "Creating…"
                    : editId
                      ? "Update"
                      : "Create Google Form"}
                </button>
              </div>
            </header>
            <div className="wa-conv-flow-body gform-builder-body">
              {error && <p className="biz-banner biz-banner-error">{error}</p>}
              <div className="wa-conv-flow-meta">
                <label className="wa-conv-flow-field">
                  <span>Form name</span>
                  <input
                    name="formname"
                    value={form.formname}
                    onChange={onChange}
                    placeholder="Customer Registration"
                    required
                    autoFocus
                  />
                </label>
                <label className="wa-conv-flow-field wa-conv-flow-triggers">
                  <span>Description</span>
                  <input
                    name="description"
                    value={form.description}
                    onChange={onChange}
                    placeholder="Register your customer details"
                  />
                </label>
              </div>

              <div className="gform-builder-canvas">
                {editId && (
                  <div className="gform-builder-row">
                    <label className="wa-conv-flow-field">
                      <span>Form URL</span>
                      <input
                        name="formurl"
                        value={form.formurl}
                        onChange={onChange}
                        required
                      />
                    </label>
                    <label className="wa-conv-flow-field">
                      <span>Form ID</span>
                      <input
                        name="formid"
                        value={form.formid}
                        onChange={onChange}
                      />
                    </label>
                  </div>
                )}

                <div className="gform-fields">
                  <div className="gform-fields-head">
                    <span>Fields</span>
                  </div>
                  {form.fields.map((field, index) => (
                    <div className="gform-field-row" key={field.key}>
                      <span className="gform-field-grip" title="Reorder">
                        <FaBars />
                      </span>
                      <input
                        value={field.title}
                        onChange={(e) => setField(field.key, { title: e.target.value })}
                        placeholder="Field name"
                        disabled={Boolean(editId)}
                        required={!editId}
                      />
                      <select
                        value={field.type}
                        onChange={(e) => setField(field.key, { type: e.target.value })}
                        disabled={Boolean(editId)}
                      >
                        {FIELD_TYPES.map((type) => (
                          <option key={type.value} value={type.value}>
                            {type.label}
                          </option>
                        ))}
                      </select>
                      <label className="gform-field-required">
                        <input
                          type="checkbox"
                          checked={field.required}
                          onChange={(e) =>
                            setField(field.key, { required: e.target.checked })
                          }
                          disabled={Boolean(editId)}
                        />
                        Required
                      </label>
                      <select
                        value={field.role}
                        onChange={(e) => setField(field.key, { role: e.target.value })}
                        disabled={Boolean(editId)}
                        title="Lead mapping"
                      >
                        <option value="">Lead field</option>
                        <option value="name">Name</option>
                        <option value="phone">WhatsApp number</option>
                      </select>
                      {!editId && (
                        <div className="gform-field-move">
                          <button
                            type="button"
                            className="biz-btn-icon"
                            title="Move up"
                            disabled={index === 0}
                            onClick={() => moveField(index, -1)}
                          >
                            <FaArrowUp />
                          </button>
                          <button
                            type="button"
                            className="biz-btn-icon"
                            title="Move down"
                            disabled={index === form.fields.length - 1}
                            onClick={() => moveField(index, 1)}
                          >
                            <FaArrowDown />
                          </button>
                          <button
                            type="button"
                            className="biz-btn-icon biz-btn-icon-danger"
                            title="Remove field"
                            onClick={() => removeField(field.key)}
                          >
                            <FaTrash />
                          </button>
                        </div>
                      )}
                      {field.type === "dropdown" && (
                        <input
                          className="gform-field-options"
                          value={field.options}
                          onChange={(e) =>
                            setField(field.key, { options: e.target.value })
                          }
                          placeholder="Dropdown options, comma separated"
                          disabled={Boolean(editId)}
                        />
                      )}
                    </div>
                  ))}
                  {!editId && (
                    <button
                      type="button"
                      className="biz-btn-secondary gform-add-field"
                      onClick={addField}
                      disabled={form.fields.length >= 15}
                    >
                      <FaPlus /> Add Field
                    </button>
                  )}
                </div>

                <fieldset className="gform-fieldset">
                  <legend>Response Destination</legend>
                  <label className="gform-radio">
                    <input
                      type="radio"
                      name="sheetMode"
                      checked={form.sheetMode === "new"}
                      onChange={() => onSheetMode("new")}
                      disabled={Boolean(editId)}
                    />
                    Create New Google Sheet
                  </label>
                  <label className="gform-radio">
                    <input
                      type="radio"
                      name="sheetMode"
                      checked={form.sheetMode === "existing"}
                      onChange={() => onSheetMode("existing")}
                      disabled={Boolean(editId)}
                    />
                    Existing Google Sheet
                  </label>
                  {form.sheetMode === "existing" && (
                    <div className="gform-builder-row">
                      <label className="wa-conv-flow-field">
                        <span>Saved sheets</span>
                        <select
                          value={form.spreadsheetId}
                          onChange={onExistingSheet}
                          disabled={Boolean(editId) && Boolean(form.spreadsheetId)}
                        >
                          <option value="">Select a sheet</option>
                          {sheets.map((sheet) => (
                            <option
                              key={sheet._id}
                              value={sheet.spreadsheetid || sheet.spreadsheetId || ""}
                            >
                              {sheet.sheetname || sheet.spreadsheetid || sheet._id}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label className="wa-conv-flow-field wa-conv-flow-triggers">
                        <span>Spreadsheet URL or ID</span>
                        <input
                          name="spreadsheetUrl"
                          value={form.spreadsheetUrl || form.spreadsheetId}
                          onChange={(e) =>
                            setForm((prev) => ({
                              ...prev,
                              spreadsheetUrl: e.target.value,
                              spreadsheetId: e.target.value,
                            }))
                          }
                          placeholder="https://docs.google.com/spreadsheets/d/..."
                          disabled={Boolean(editId)}
                        />
                      </label>
                    </div>
                  )}
                  {editId && form.spreadsheetUrl && (
                    <p className="gform-hint">
                      Sheet:{" "}
                      <a href={form.spreadsheetUrl} target="_blank" rel="noreferrer">
                        {form.spreadsheetUrl}
                      </a>
                    </p>
                  )}
                </fieldset>

                <fieldset className="gform-fieldset">
                  <legend>On Submit</legend>
                  <label className="gform-radio">
                    <input
                      type="checkbox"
                      name="callApi"
                      checked={form.callApi}
                      onChange={onChange}
                    />
                    Call API
                  </label>
                  {form.callApi && (
                    <label className="wa-conv-flow-field">
                      <span>Approved WhatsApp template</span>
                      <select
                        value={form.templateId}
                        onChange={onTemplate}
                        required={form.callApi}
                      >
                        <option value="">
                          {loadingTemplates
                            ? "Loading templates…"
                            : "Select approved template"}
                        </option>
                        {templates.map((tpl) => (
                          <option key={tpl._id || tpl.id} value={tpl._id || tpl.id}>
                            {tpl.name}
                            {tpl.language ? ` (${tpl.language})` : ""}
                          </option>
                        ))}
                      </select>
                    </label>
                  )}
                  {form.callApi && (preview || form.templateName) && (
                    <div className="gform-template-preview">
                      <p className="gform-template-preview-meta">
                        {[
                          preview?.name || form.templateName,
                          preview?.category,
                          preview?.language,
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      </p>
                      {preview?.headerFormat && preview.headerFormat !== "TEXT" ? (
                        <p className="gform-template-preview-media">
                          {preview.headerFormat} header
                        </p>
                      ) : null}
                      {preview?.headerText ? (
                        <p className="gform-template-preview-header">
                          {preview.headerText}
                        </p>
                      ) : null}
                      <p className="gform-template-preview-body">
                        {preview?.body || form.templateName}
                      </p>
                      {preview?.footer ? (
                        <p className="gform-template-preview-footer">{preview.footer}</p>
                      ) : null}
                      {preview?.buttons?.length > 0 ? (
                        <ul className="gform-template-preview-buttons">
                          {preview.buttons.map((text) => (
                            <li key={text}>{text}</li>
                          ))}
                        </ul>
                      ) : null}
                    </div>
                  )}
                  {form.callApi && !loadingTemplates && templates.length === 0 && (
                    <p className="gform-hint">
                      No approved WhatsApp templates for this project. Approve a
                      template in Template Manager first.
                    </p>
                  )}
                  {form.callApi && (
                    <p className="gform-hint">
                      On each new submission, the selected template is sent with{" "}
                      <code>/getInteractedCustomerDetail/sendTemplate</code> to the
                      form WhatsApp number.
                    </p>
                  )}
                </fieldset>
              </div>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default GoogleFormConfig;
